import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { isRegistrationOpen, type TournamentStatus } from '@/core/schemas';
import { describeError } from '@/features/team/describeError';
import { useTournamentData } from '@/features/tournament/useTournamentData';
import { useAutoDismiss } from '@/shared/hooks/useAutoDismiss';
import { usePageTitle } from '@/shared/hooks/usePageTitle';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { LoadingScreen } from '@/shared/ui/LoadingScreen';
import { PageState } from '@/shared/ui/PageState';

import { AdminTournamentSetup } from './AdminTournamentSetup';
import { describeBlocked, describeReconcile } from './progressionNotice';
import { useAdminActions } from './useAdminActions';

const STATUS_LABELS: Readonly<Record<TournamentStatus, string>> = {
  setup: 'Einrichtung',
  // Not "Anmeldung offen": whether it is actually open is its own tile next to
  // this one, and the two disagreed on screen.
  registration: 'Anmeldephase',
  running: 'läuft',
  paused: 'pausiert',
  completed: 'abgeschlossen',
  archived: 'archiviert',
};

export function AdminControlPage() {
  const { tournament, teams, matches, lookup, isLoading, errorMessage, tournamentError } =
    useTournamentData();
  const actions = useAdminActions();
  const [confirmReset, setConfirmReset] = useState(false);
  const [notice, setNotice] = useAutoDismiss();
  // Deliberately not auto-dismissed: this one asks the director to go and
  // check a board, which takes longer than a toast lives.
  const [warning, setWarning] = useState<string | null>(null);

  usePageTitle('Turnierleitung');

  const start = useMutation({
    mutationFn: () => {
      if (tournament === undefined) throw new Error('Kein Turnier');
      return actions.start(tournament, teams);
    },
    onSuccess: (result) => setNotice(`Turnier gestartet — ${result.matchCount} Spiele erzeugt.`),
  });

  const registration = useMutation({
    mutationFn: (open: boolean) => {
      if (tournament === undefined) throw new Error('Kein Turnier');
      return open ? actions.openRegistration(tournament) : actions.closeRegistration(tournament);
    },
    onSuccess: (_data, open) =>
      setNotice(open ? 'Die Anmeldung ist geöffnet.' : 'Die Anmeldung ist geschlossen.'),
  });

  const pauseResume = useMutation({
    mutationFn: (paused: boolean) => {
      if (tournament === undefined) throw new Error('Kein Turnier');
      return paused ? actions.pause(tournament) : actions.resume(tournament);
    },
  });

  const reset = useMutation({
    mutationFn: () => {
      if (tournament === undefined) throw new Error('Kein Turnier');
      return actions.reset(tournament, matches);
    },
    onSuccess: (count) => {
      setNotice(`Turnier zurückgesetzt — ${count} Spiele gelöscht.`);
      setConfirmReset(false);
    },
  });

  const reconcile = useMutation({
    mutationFn: () => {
      if (tournament === undefined) throw new Error('Kein Turnier');
      return actions.reconcile(tournament, teams, matches);
    },
    onSuccess: (outcome) => {
      setNotice(describeReconcile(outcome));
      setWarning(describeBlocked(outcome.progression.blocked, matches, lookup));
    },
  });

  if (isLoading) {
    return <LoadingScreen label="Turnierdaten werden geladen …" />;
  }

  // Empty deployment: no tournament document yet. Instead of a dead-end error
  // page, offer the admin the create form for this deployment's fixed id.
  if (tournamentError?.kind === 'not-found') {
    return <AdminTournamentSetup />;
  }

  if (errorMessage !== null || tournament === undefined) {
    return <PageState title="Turnier nicht verfügbar" description={errorMessage ?? ''} />;
  }

  const status = tournament.status;
  const activeTeams = teams.filter((team) => team.status === 'active').length;
  const openConflicts = matches.filter((match) => match.status === 'conflict').length;
  const registrationOpen = isRegistrationOpen(tournament);
  const error =
    describeError(start.error) ??
    describeError(registration.error) ??
    describeError(pauseResume.error) ??
    describeError(reset.error) ??
    describeError(reconcile.error);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-4 sm:grid-cols-4">
        <Stat label="Status" value={STATUS_LABELS[status]} />
        <Stat label="Anmeldung" value={registrationOpen ? 'geöffnet' : 'geschlossen'} />
        <Stat label="Angemeldete Teams" value={String(activeTeams)} />
        <Stat
          label="Offene Konflikte"
          value={String(openConflicts)}
          tone={openConflicts > 0 ? 'danger' : 'default'}
        />
      </div>

      {notice !== null && <Alert tone="success">{notice}</Alert>}
      {warning !== null && <Alert tone="warning">{warning}</Alert>}
      {error !== null && <Alert tone="danger">{error}</Alert>}

      <section className="border-line bg-surface flex flex-col gap-4 rounded-xl border p-5">
        <h2 className="text-lg font-semibold">Turniersteuerung</h2>

        <div className="flex flex-wrap gap-3">
          {(status === 'setup' || status === 'registration') && (
            <Button
              variant="secondary"
              onClick={() => registration.mutate(!registrationOpen)}
              isLoading={registration.isPending}
            >
              {registrationOpen ? 'Anmeldung schließen' : 'Anmeldung öffnen'}
            </Button>
          )}

          {(status === 'setup' || status === 'registration') && (
            <Button
              onClick={() => start.mutate()}
              isLoading={start.isPending}
              disabled={activeTeams < 2}
            >
              Turnier starten
            </Button>
          )}

          {status === 'running' && (
            <Button
              variant="secondary"
              onClick={() => pauseResume.mutate(true)}
              isLoading={pauseResume.isPending}
            >
              Pausieren
            </Button>
          )}

          {status === 'paused' && (
            <Button onClick={() => pauseResume.mutate(false)} isLoading={pauseResume.isPending}>
              Fortsetzen
            </Button>
          )}

          {/* Also while paused: a correction is exactly what one pauses for. */}
          {(status === 'running' || status === 'paused') && (
            <Button
              variant="secondary"
              onClick={() => reconcile.mutate()}
              isLoading={reconcile.isPending}
            >
              Turnierbaum aktualisieren / nächste Phase
            </Button>
          )}

          {status !== 'setup' && status !== 'registration' && (
            <Button variant="danger" onClick={() => setConfirmReset(true)}>
              Zurücksetzen
            </Button>
          )}
        </div>

        {(status === 'setup' || status === 'registration') && !registrationOpen && (
          <p className="text-fg-subtle text-sm">
            Solange die Anmeldung geschlossen ist, kann kein Kapitän ein Team anmelden.
          </p>
        )}

        {activeTeams < 2 && (status === 'setup' || status === 'registration') && (
          <p className="text-fg-subtle text-sm">
            Für den Start werden mindestens zwei angemeldete Teams benötigt.
          </p>
        )}

        {(status === 'running' || status === 'paused') && (
          <p className="text-fg-subtle text-sm">
            „Turnierbaum aktualisieren" trägt Sieger in die KO-Runde ein und plant die nächste
            Phase, sobald die aktuelle vollständig gespielt ist. Läuft normalerweise automatisch;
            der Knopf ist die Rückfallebene und nach jeder Ergebniskorrektur gefahrlos nutzbar.
          </p>
        )}
      </section>

      <ConfirmDialog
        open={confirmReset}
        title="Turnier zurücksetzen?"
        description="Alle Spiele und Ergebnisse werden gelöscht. Teams und Einstellungen bleiben erhalten. Dieser Schritt kann nicht rückgängig gemacht werden."
        confirmLabel="Endgültig zurücksetzen"
        tone="danger"
        isBusy={reset.isPending}
        onConfirm={() => reset.mutate()}
        onCancel={() => setConfirmReset(false)}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'danger';
}) {
  return (
    <div className="border-line bg-surface rounded-xl border p-4">
      <p className="text-fg-subtle text-xs tracking-wide uppercase">{label}</p>
      <p
        className={
          tone === 'danger' ? 'text-danger mt-1 text-2xl font-bold' : 'mt-1 text-2xl font-bold'
        }
      >
        {value}
      </p>
    </div>
  );
}
